import { createContext, createElement, useContext, useMemo, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { useApi } from '../../../lib/api';
import { fetchJson } from '../../../lib/apiFetchJson';
import { useStreamInvalidation } from '../../../view-system/hooks/useStreamInvalidation';
import {
  PhoneRequestStatesResponseSchema,
  MAX_PHONE_REQUEST_CONTACTS,
  PHONE_REQUEST_DEADLINE_MS,
  PHONE_REQUEST_INVALIDATE_REASON,
  StartPhoneRequestsResponseSchema,
  type MarkPhoneWrongBody,
  type PhoneRequestState,
  type StartPhoneRequestsBody,
  type StartPhoneRequestsResponse,
} from '@shared/schemas/enrichment-staging';
import { flowListInvalidationKeys } from '../flow-list-invalidation';

/**
 * Phone-staging row state (U14) — one batched read for every visible contact,
 * kept live by the `phone_request` SSE invalidate (prod) and a 3 s poll only while
 * a live request (`finding`, within its deadline) is in the set; one fetch on mount / id-set change, and
 * `useStartPhoneSearch` refetches at once on success. Each request carries at most
 * MAX_PHONE_REQUEST_CONTACTS ids. (Localhost has no callback webhook, so the poll
 * is what finalizes a search there.)
 *
 * TanStack's cache is the store: no module-scope state.
 */
export const PHONE_REQUESTS_KEY = ['flow', 'phone-requests'] as const;

/**
 * Keys refreshed with the phone state: the lists that show a contact's best phone (Signals, People) plus the
 * favorite counts, in that order. The staged-phone overlay changes `best_phone` on those rows, and a saved view
 * filtering on phone counts those same rows. Derived from `flowListInvalidationKeys` so it can never drift from
 * what the lists' own mutations invalidate. Kept a key ARRAY because `useEnrichOnDial` also reads it (an
 * `useIsFetching` predicate on each key's root) beside the invalidation here and in `useStreamInvalidation`.
 */
export const PHONE_LIST_KEYS = flowListInvalidationKeys('signal', 'contact');

const FINDING_POLL_MS = 3000;
/** Past a request's own deadline the server has expired it; the grace covers the reaper tick and clock skew. */
const FINDING_CARRY_GRACE_MS = 60_000;
/** A `finding` row older than this is a stuck request the server has not expired yet. */
export const FINDING_LIVE_WINDOW_MS = PHONE_REQUEST_DEADLINE_MS + FINDING_CARRY_GRACE_MS;

/** `finding` and still within the request's deadline plus grace (see `refetchInterval`). */
function isLiveFinding(s: PhoneRequestState): boolean {
  return s.state === 'finding' && Date.now() - new Date(s.updated_at).getTime() < FINDING_LIVE_WINDOW_MS;
}

const EMPTY_STATES: readonly PhoneRequestState[] = [];

function chunk<T>(items: readonly T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += size) out.push(items.slice(i, i + size));
  return out;
}

export interface PhoneRequestStates {
  byContactId: Map<string, PhoneRequestState>;
  findingContactIds: Set<string>;
  readyContactIds: Set<string>;
  isLoading: boolean;
}

/** A contact left `finding`, or a `ready` value appeared or changed, between two reads of the same id set. */
function searchCompleted(previous: readonly PhoneRequestState[], next: readonly PhoneRequestState[]): boolean {
  const before = new Map(previous.map((s) => [s.contact_id, s]));
  const after = new Map(next.map((s) => [s.contact_id, s]));
  for (const [contactId, was] of before) {
    const now = after.get(contactId);
    if (was.state === 'finding' && now?.state !== 'finding') return true;
    if (was.state === 'ready' && now?.state === 'ready' && was.value !== now.value) return true;
  }
  for (const [contactId, now] of after) if (now.state === 'ready' && !before.has(contactId)) return true;
  return false;
}

export function usePhoneRequestStates(contactIds: readonly string[]): PhoneRequestStates {
  const { apiFetch } = useApi();
  // Order-insensitive and de-duplicated: the same set in any order is one cache entry.
  const sortedIds = useMemo(() => [...new Set(contactIds)].sort(), [contactIds]);
  const idsKey = sortedIds.join(',');

  const queryClient = useQueryClient();
  const queryKey = useMemo(() => [...PHONE_REQUESTS_KEY, idsKey] as const, [idsKey]);
  const query = useQuery<PhoneRequestState[]>({
    queryKey,
    enabled: sortedIds.length > 0,
    // Cache-indefinitely + invalidate on `phone_request` / mutation (CLAUDE.md caching rule).
    staleTime: Infinity,
    // Poll only while a request in the visible set is genuinely open. A `finding` row older than its own deadline
    // plus the grace is a stuck request the server has not expired yet: it still shows as finding, but it must not
    // keep every viewer of that contact polling a hundreds-of-ids list; the reaper's `phone_request` frame (or the
    // next mount / id-set change) refreshes it.
    refetchInterval: (q) => (q.state.data?.some(isLiveFinding) ? FINDING_POLL_MS : false),
    queryFn: async () => {
      // Previous read of THIS id set (the id set is in the query key, so a different set is its own baseline).
      const previous = queryClient.getQueryData<PhoneRequestState[]>(queryKey);
      const pages = await Promise.all(
        chunk(sortedIds, MAX_PHONE_REQUEST_CONTACTS).map((ids) =>
          fetchJson(
            apiFetch,
            PhoneRequestStatesResponseSchema,
            `/api/flow/phone-requests?contact_ids=${encodeURIComponent(ids.join(','))}`
          )
        )
      );
      const read = pages.flat();
      // The server answers 200 [] when its store errors (fail-open), and a contact absent from a read would otherwise
      // read as "no longer finding" and end this client's polling for a search that is still running. Carry a
      // previously-finding contact forward, unchanged, until the request's own deadline plus a grace has passed.
      const nowInRead = new Set(read.map((s) => s.contact_id));
      const maxAgeMs = FINDING_LIVE_WINDOW_MS;
      const carried = (previous ?? []).filter(
        (s) => s.state === 'finding' && !nowInRead.has(s.contact_id) && Date.now() - new Date(s.updated_at).getTime() < maxAgeMs,
      );
      const next = carried.length > 0 ? [...read, ...carried] : read;
      // Refresh the phone-bearing lists when THIS client observes a search finish (a joiner rep never gets the
      // server's phone_request frame for a request another rep started). The first read is only a baseline.
      if (previous && searchCompleted(previous, next)) {
        for (const listKey of PHONE_LIST_KEYS) void queryClient.invalidateQueries({ queryKey: listKey });
      }
      return next;
    },
  });

  const states = sortedIds.length > 0 ? (query.data ?? EMPTY_STATES) : EMPTY_STATES;

  return useMemo(() => {
    const byContactId = new Map<string, PhoneRequestState>();
    const findingContactIds = new Set<string>();
    const readyContactIds = new Set<string>();
    for (const s of states) {
      byContactId.set(s.contact_id, s);
      if (s.state === 'finding') findingContactIds.add(s.contact_id);
      else if (s.state === 'ready') readyContactIds.add(s.contact_id);
    }
    return { byContactId, findingContactIds, readyContactIds, isLoading: query.isLoading };
  }, [states, query.isLoading]);
}

/**
 * Refreshes row states (and the phone-bearing lists) on `phone_request`, and
 * on a stream reconnect (the server does not replay frames). Mounted once at
 * shell level beside `useInvalidateRequestsOnStream`.
 */
export function useInvalidatePhoneRequestsOnStream(): void {
  useStreamInvalidation({
    when: (event) => event.type === 'invalidate' && event.reason === PHONE_REQUEST_INVALIDATE_REASON,
    keys: [PHONE_REQUESTS_KEY, ...PHONE_LIST_KEYS],
  });
}

function invalidatePhoneCaches(queryClient: QueryClient): Promise<unknown> {
  return Promise.all(
    [PHONE_REQUESTS_KEY, ...PHONE_LIST_KEYS].map((queryKey) => queryClient.invalidateQueries({ queryKey }))
  );
}

/** A 200-contact start dispatches ~40 s server-side (5 at a time); the default 15 s client timeout would abort a good start. */
const START_PHONE_SEARCH_TIMEOUT_MS = 90_000;

export type StartPhoneSearchResponse = StartPhoneRequestsResponse;

/**
 * Start a number search. Errors are NOT handled here: the global
 * `MutationCache.onError` (`lib/mutation-cache-handler.ts`) toasts the
 * server's `problem()` title via `notifyError`, and `apiFetch` already toasts
 * a `WriteRefusedError` (View as). No status code ever reaches the rep.
 */
export function useStartPhoneSearch() {
  const { apiFetch } = useApi();
  const queryClient = useQueryClient();
  return useMutation<StartPhoneSearchResponse, Error, StartPhoneRequestsBody>({
    mutationFn: (body) =>
      fetchJson(apiFetch, StartPhoneRequestsResponseSchema, '/api/flow/phone-requests', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
        timeoutMs: START_PHONE_SEARCH_TIMEOUT_MS,
      }),
    onSuccess: () => invalidatePhoneCaches(queryClient),
  });
}

/** Mark a staged/found number wrong for one contact (204 on success). */
export function useMarkPhoneWrong() {
  const { apiFetch } = useApi();
  const queryClient = useQueryClient();
  return useMutation<void, Error, { contactId: string } & MarkPhoneWrongBody>({
    mutationFn: async ({ contactId, value }) => {
      await fetchJson(apiFetch, z.undefined(), `/api/flow/contacts/${encodeURIComponent(contactId)}/phone/wrong`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ value }),
      });
    },
    onSuccess: () => invalidatePhoneCaches(queryClient),
  });
}

/**
 * One batched `usePhoneRequestStates(contactIds)` call for a page of rows, so each
 * row reads from a single query. Buttons outside a provider fall back to their own
 * one-id read (TanStack dedupes identical keys).
 */
const PhoneStatesContext = createContext<Map<string, PhoneRequestState> | null>(null);

export function PhoneRequestStatesProvider({
  contactIds,
  children,
}: {
  contactIds: readonly string[];
  children: ReactNode;
}) {
  const { byContactId } = usePhoneRequestStates(contactIds);
  return createElement(PhoneStatesContext.Provider, { value: byContactId }, children);
}

/** One contact's phone-search state: from a surrounding `PhoneRequestStatesProvider` when there is one, else its own one-id read. */
export function usePhoneRequestState(contactId: string | null | undefined): PhoneRequestState | undefined {
  const fromContext = useContext(PhoneStatesContext);
  // Always call the hook; with no ids it is disabled, so inside a provider it costs nothing.
  const own = usePhoneRequestStates(fromContext || !contactId ? [] : [contactId]);
  return contactId ? (fromContext ?? own.byContactId).get(contactId) : undefined;
}
